// src/pages/misc/public/ComoFuncionaPage.tsx
// [HANDOFF] HOW IT WORKS (/como-funciona). Wide layout. Top: a search box + topic chips (Todo + the topics from learnContent.ts). Both filter at
//   once (topic AND text in title / hook / note or title / excerpt). Left column: "Videos" cards, then "Artículos" cards (each links to
//   /como-funciona/<slug>). Right column: "Síguenos" social links. Clicking a video opens a popup with left / right arrows that cycle
//   through the CURRENT filtered list (or all videos when the filter is empty). The popup reuses the landing's video styles (.vl-vid*).
// [VERIFY] No video plays: every video shows "Video en camino" (same as landing/LandingVideoModal.tsx). The four social links below
//   (instagram.com/ventaje, facebook.com/ventaje, linkedin.com/company/ventaje, youtube.com/@ventaje) are guessed addresses, confirm them.
// [URL-RENAME] /como-funciona -> /how-it-works, and the /como-funciona/<slug> links made below.
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Play, Search, X } from 'lucide-react';
import PublicShell from './PublicShell';
import { ARTICLES, TOPICS, VIDEOS, type LearnVideo, type Topic } from './learnContent';

const ComoFuncionaPage = () => {
  const [q, setQ] = useState('');
  const [topic, setTopic] = useState<Topic | 'Todo'>('Todo');
  const [video, setVideo] = useState<LearnVideo | null>(null);

  const query = q.trim().toLowerCase();
  const match = (title: string, extra: string, itemTopic: Topic) => {
    if (topic !== 'Todo' && itemTopic !== topic) return false;
    if (!query) return true;
    return `${title} ${extra}`.toLowerCase().includes(query);
  };

  const videos = useMemo(
    () => VIDEOS.filter((v) => match(v.title, `${v.hook} ${v.note}`, v.topic)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [q, topic],
  );
  const articles = useMemo(
    () => ARTICLES.filter((a) => match(a.title, a.excerpt, a.topic)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [q, topic],
  );

  const openAt = (step: number) => {
    if (!video) return;
    const list = videos.length ? videos : VIDEOS;
    const i = list.findIndex((v) => v.id === video.id);
    const next = list[(i + step + list.length) % list.length];
    setVideo(next);
  };

  return (
    <PublicShell
      wide
      pageTitle="Cómo funciona"
      kicker="Cómo funciona"
      title={<>Explicado fácil, <b>en unos minutos.</b></>}
      lead="Videos y artículos sobre Ventaje: qué es un CRM, las tres páginas, y qué pasa con tu equipo."
    >
      <div className="pub-tools">
        <label className="pub-search">
          <Search size={16} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Busca un tema, por ejemplo WhatsApp o ficha" />
        </label>
        <div className="pub-topics">
          {(['Todo', ...TOPICS] as const).map((t) => (
            <button key={t} type="button" className={topic === t ? 'on' : ''} onClick={() => setTopic(t)}>{t}</button>
          ))}
        </div>
      </div>

      <div className="pub-layout">
        <div>
          <h2 className="pub-h">Videos</h2>
          <div className="pub-feed">
            {videos.map((v) => (
              <button key={v.id} type="button" className="pub-thumb" onClick={() => setVideo(v)}>
                <span className="pub-thumb-art" data-tone={v.topic}>
                  <Play size={18} fill="currentColor" />
                  <em>{v.hook}</em>
                </span>
                <span className="pub-thumb-meta">
                  <small>{v.topic} · {v.duration}</small>
                  <strong>{v.title}</strong>
                  <span>Ver video</span>
                </span>
              </button>
            ))}
          </div>

          <h2 className="pub-h">Artículos</h2>
          <div className="pub-feed">
            {articles.map((a) => (
              <Link key={a.slug} className="pub-article" to={`/como-funciona/${a.slug}`}>
                <small>{a.topic} · {a.minutes} min</small>
                <strong>{a.title}</strong>
                <p>{a.excerpt}</p>
                <span>Leer</span>
              </Link>
            ))}
          </div>
          {videos.length === 0 && articles.length === 0 ? (
            <p className="pub-note">Nada con esa búsqueda. Prueba otra palabra o “Todo”.</p>
          ) : null}
        </div>

        {/* "Síguenos" is not rendered: the four social addresses are the designer's guesses (the [VERIFY] note at the top)
            and the real ones are owed by Dante. When he supplies them the aside returns here, in the right column .pub-layout
            reserves for it (public.css): <aside className="pub-side"> with one <a target="_blank" rel="noreferrer"> per network. */}
      </div>

      {video && createPortal(
        <div className="vl-vid-backdrop" onClick={() => setVideo(null)} role="presentation">
          <div className="pub-vid-stage" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="pub-vid-arrow is-prev" aria-label="Video anterior" onClick={() => openAt(-1)}><ChevronLeft size={26} /></button>
            <div className="vl-vid" role="dialog" aria-modal="true" aria-label={video.title}>
              <button type="button" className="vl-vid-close" aria-label="Cerrar" onClick={() => setVideo(null)}><X size={18} /></button>
              <div className="vl-vid-frame">
                <div className="vl-vid-empty">
                  <span className="vl-vid-play"><Play size={22} fill="currentColor" /></span>
                  <small>Video en camino · {video.duration}</small>
                </div>
              </div>
              <div className="vl-vid-meta">
                <span className="vl-tag">{video.duration}</span>
                <h3>{video.title}</h3>
                <p>{video.note}</p>
              </div>
            </div>
            <button type="button" className="pub-vid-arrow is-next" aria-label="Video siguiente" onClick={() => openAt(1)}><ChevronRight size={26} /></button>
          </div>
        </div>,
        document.body,
      )}
    </PublicShell>
  );
};

export default ComoFuncionaPage;
